"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertCircle, IconBellPlus, IconRosetteDiscountCheck } from "@tabler/icons-react";
import { skipToken, useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { LinkPendingIcon } from "@/components/link-pending-icon";
import { addDomainResumeHref } from "@/lib/add-domain-resume";
import { loginHref } from "@/lib/safe-next-path";
import { useTRPC } from "@/lib/trpc/client";
import { useSession } from "@domainstack/auth/client";
import { Button } from "@domainstack/ui/button";
import { useIsClient } from "@domainstack/ui/hooks";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
type TrackDomainButtonProps = {
  domain: string;
  /** Whether the button is enabled. Defaults to true. */
  enabled?: boolean;
};
function TrackedVerifiedButton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Tooltip><TooltipTrigger render={<Button variant="outline" nativeButton={false} aria-label="View in dashboard" render={<Link href="/dashboard"><IconRosetteDiscountCheck className="text-success-foreground" aria-hidden="true" /><span className="sr-only">View in dashboard</span></Link>} />} /><TooltipContent>You own and track this domain</TooltipContent></Tooltip>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function TrackOrVerifyButton(t0) {
  const $ = _c(18);
  const {
    isPendingVerification,
    href
  } = t0;
  const label = isPendingVerification ? "Verify" : "Track";
  let t1;
  if ($[0] !== isPendingVerification) {
    t1 = isPendingVerification ? <IconAlertCircle className="text-accent-orange" aria-hidden="true" /> : <IconBellPlus className="sm:text-muted-foreground" aria-hidden="true" />;
    $[0] = isPendingVerification;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const icon = t1;
  const tooltipText = isPendingVerification ? "Complete verification for this domain" : "Get alerts for this domain";
  const ariaLabel = isPendingVerification ? "Verify domain" : "Track domain";
  let t2;
  if ($[2] !== icon) {
    t2 = <LinkPendingIcon icon={icon} />;
    $[2] = icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label) {
    t3 = <span className="hidden sm:inline">{label}</span>;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== href || $[7] !== t2 || $[8] !== t3) {
    t4 = <Link href={href} scroll={false}>{t2}{t3}</Link>;
    $[6] = href;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== ariaLabel || $[11] !== t4) {
    t5 = <TooltipTrigger render={<Button variant="outline" nativeButton={false} aria-label={ariaLabel} render={t4} />} />;
    $[10] = ariaLabel;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== tooltipText) {
    t6 = <TooltipContent><p>{tooltipText}</p></TooltipContent>;
    $[13] = tooltipText;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <Tooltip>{t5}{t6}</Tooltip>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
export function TrackDomainButton(t0) {
  const $ = _c(15);
  const {
    domain,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    data: session,
    isPending: isSessionPending
  } = useSession();
  const trpc = useTRPC();
  const mounted = useIsClient();
  const isAuthenticated = !!session?.user;
  let t2;
  if ($[0] !== domain || $[1] !== isAuthenticated || $[2] !== trpc.tracking.getTrackingStatus) {
    t2 = trpc.tracking.getTrackingStatus.queryOptions(isAuthenticated ? {
      domain
    } : skipToken);
    $[0] = domain;
    $[1] = isAuthenticated;
    $[2] = trpc.tracking.getTrackingStatus;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: trackedDomain,
    isLoading: isLoadingStatus
  } = useQuery(t2);
  const isTracked = !!trackedDomain;
  const isVerified = trackedDomain?.verified ?? false;
  const isPendingVerification = isTracked && !isVerified;
  if (!mounted || isSessionPending || !enabled || session?.user && isLoadingStatus) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Button variant="outline" disabled={true} aria-label="Track domain"><IconBellPlus className="sm:text-muted-foreground" aria-hidden="true" /><span className="hidden sm:inline">Track</span></Button>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (isTracked && isVerified) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <TrackedVerifiedButton />;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  const t3 = `/dashboard/add-domain?domain=${encodeURIComponent(domain)}`;
  let t4;
  if ($[6] !== t3) {
    t4 = loginHref(t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let href = t4;
  if (isAuthenticated) {
    let t5;
    if ($[8] !== domain || $[9] !== isPendingVerification || $[10] !== trackedDomain) {
      t5 = isPendingVerification && trackedDomain ? addDomainResumeHref(trackedDomain.id, trackedDomain.verificationMethod) : `/dashboard/add-domain?domain=${encodeURIComponent(domain)}`;
      $[8] = domain;
      $[9] = isPendingVerification;
      $[10] = trackedDomain;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    href = t5;
  }
  let t5;
  if ($[12] !== href || $[13] !== isPendingVerification) {
    t5 = <TrackOrVerifyButton isPendingVerification={isPendingVerification} href={href} />;
    $[12] = href;
    $[13] = isPendingVerification;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}